import { Emitter } from './emitter.js'
import { CALL_STATE } from '@pine/protocol'
import { fromDevice, now as clockNow } from './clock.js'

/**
 * Voice and video calls.
 *
 * Media is plain WebRTC, so it goes directly between devices and is DTLS-SRTP
 * encrypted end to end. What is unusual is the signalling: there is no
 * signalling server. Offers, answers and ICE candidates travel over the room's
 * own encrypted peer connections (see ephemeral.js), each frame signed with
 * the sender's account key, so a peer cannot fake an incoming call from
 * someone else.
 *
 * Topology is a full mesh: every participant holds a connection to every
 * other one. With no server in the middle there is nothing to mix the streams,
 * so each person sends their own picture once per listener — which is what
 * decides how large a call can be, and why the video that goes out is scaled
 * down as the room fills (see _applyQuality). Thirty is the ceiling; a call
 * that size is mostly listeners, and the maths is spelt out on MESH_LIMIT.
 */

export const SIGNAL = {
  RING: 'ring',
  ACCEPT: 'accept',
  DECLINE: 'decline',
  OFFER: 'offer',
  ANSWER: 'answer',
  ICE: 'ice',
  LEAVE: 'leave',
  END: 'end',
  MEDIA: 'media',
  // "Is a call already running in this room?" and its answer. What lets
  // somebody who missed the ring — or hung up and changed their mind — walk
  // into a call that is already going, instead of starting a second one
  // beside it.
  HELLO: 'hello',
  HERE: 'here'
}

/**
 * How many people can be in one call.
 *
 * A mesh costs every participant one outgoing copy of their own stream per
 * other participant. At thirty that is 29 copies up and 29 streams down, which
 * is why video is scaled hard as the count climbs: at the top of the range
 * each stream is capped near 120 kbit/s and a quarter resolution, so the
 * uplink lands around 3.5 Mbit/s rather than the 35 the same call would cost
 * at full size. Audio is about 30 kbit/s each and is never scaled.
 *
 * Be honest about what this means in practice: thirty works on a good
 * connection with most cameras off, and a phone doing thirty video decodes
 * will get hot. Six people with cameras on is a comfortable call; thirty is a
 * meeting where a few people talk.
 */
export const MESH_LIMIT = 30

/**
 * Outgoing video, sized to how full the call is.
 *
 * `scale` divides each side of the picture, so 2 is a quarter of the pixels.
 * Applied to every sender whenever somebody joins or leaves, which is the
 * only moment the arithmetic changes.
 */
/**
 * How a call is offered to a phone that is asleep.
 *
 * A RING travels over the room's live connections and is not stored anywhere,
 * so a phone that was not connected at the instant it was sent never hears
 * it. That is exactly the phone a push notification has just woken up: by the
 * time its app has started, joined the room and found the caller, the one
 * RING is long gone, and the call it was woken for never rings.
 *
 * So the caller keeps offering it, every few seconds, until somebody answers
 * or the call gives up. The receiving side already ignores a call it has seen
 * (CallManager._seen), so the repeats cost nothing to anyone already ringing.
 */
export const RING_EVERY = 3000
export const RING_FOR = 60000
/* How often a call nobody has answered yet wakes the phones it is ringing. */
export const CALL_WAKE_EVERY = 10000
/* And how often it looks the room up again while nobody in it is reachable. */
export const CALL_LOOK_EVERY = 8000

/*
 * A call whose media path broke — the network changed under one of the two
 * phones — is mended by offering a new path (an ICE restart) every few
 * seconds, until it connects again or this long has gone by.
 */
export const HEAL_EVERY = 4000
export const HEAL_FOR = 60 * 1000

/*
 * Setting a call up, when something on the way is lost.
 *
 * The signals that set a call up (accept, offer, answer, candidates) each
 * cross the room once. One lost on a link that was being replaced (a phone
 * just woken by a push, a network change) used to leave the call on
 * "connecting" for good: nothing on either side ever asked again, and
 * nothing ever gave up until somebody pressed the red button.
 *
 * Now the person who answered says so again every ACCEPT_EVERY until an
 * offer arrives; a connection that is not up after CONNECT_WAIT is offered
 * a new path (_heal); and a call with nobody connected after CONNECT_FOR
 * ends and says it could not connect.
 */
export const ACCEPT_EVERY = 3000

/*
 * "Answer" or "decline" pressed on a call the phone rang for from the push
 * alone (server/relay/lib/push.js, ringExtras), before the call itself had
 * reached the app. Held this long for the ring to arrive and be applied.
 */
export const PENDING_ANSWER_FOR = 60 * 1000
export const CONNECT_WAIT = 10000
export const CONNECT_FOR = 45000
// "disconnected" often mends itself within a second or two; this long is
// given before anybody steps in.
const HEAL_AFTER = 3000

const QUALITY = [
  { upTo: 1, bitrate: 1200000, scale: 1 },
  { upTo: 3, bitrate: 700000, scale: 1 },
  { upTo: 7, bitrate: 400000, scale: 2 },
  { upTo: 15, bitrate: 220000, scale: 3 },
  { upTo: Infinity, bitrate: 120000, scale: 4 }
]

export const DEFAULT_ICE_SERVERS = [
  { urls: 'stun:papi.olshub.xyz:3478' },
  { urls: 'stun:stun.cloudflare.com:3478' },
  { urls: 'stun:stun.l.google.com:19302' }
]

const hasWebRTC = typeof globalThis.RTCPeerConnection !== 'undefined'

/**
 * One live call inside one room.
 *
 * @param room       a Room from @pine/core
 * @param iceServers RTCIceServer[] — include TURN credentials for the ~10%
 *                   of networks that symmetric NAT makes undialable
 */
export class Call extends Emitter {
  constructor (room, {
    callId, video = false, iceServers = DEFAULT_ICE_SERVERS, initiator = false, members = 2,
    ringEvery = RING_EVERY, ringFor = RING_FOR,
    acceptEvery = ACCEPT_EVERY, connectWait = CONNECT_WAIT, connectFor = CONNECT_FOR
  } = {}) {
    super()
    if (!hasWebRTC) throw new Error('This runtime has no WebRTC — calls need a browser, WebView or Electron window')

    this.room = room
    this.id = callId
    this.video = video
    this.initiator = initiator

    /*
     * The ICE servers may arrive after the call is made: an array, or a
     * promise of one. Fresh TURN credentials (ice.js) used to be fetched
     * before the call existed, so before the ring went out on the phone
     * calling and before the ring was shown on the phone called. On a phone
     * just woken by a push that was up to five seconds of a call already
     * ringing on the other end and nothing on screen. Now the call goes
     * ahead at once, and only building a connection waits for them.
     */
    this.iceServers = Array.isArray(iceServers) && iceServers.length ? iceServers : DEFAULT_ICE_SERVERS
    this._iceReady = Promise.resolve(iceServers).then((servers) => {
      if (Array.isArray(servers) && servers.length) this.iceServers = servers
      return this.iceServers
    }, () => this.iceServers)
    // Connections started before anybody picked up; see warm().
    this._warm = new Map()

    /*
     * How many people the room holds, and so whether this is a conversation
     * or a meeting. It changes three things: a decline from one person does
     * not cancel a group call, hanging up leaves it instead of ending it for
     * everybody, and the video is scaled for the crowd.
     */
    this.members = Math.max(2, members)
    this.group = this.members > 2

    // Whether we are *in* the call, as opposed to being rung by it. Everything
    // that negotiates media hangs off this: a phone that is still ringing has
    // no microphone open and must not start answering offers on behalf of
    // somebody who has not picked up yet.
    this.joined = initiator
    this.declinedBy = new Set()
    this.ringEvery = ringEvery
    this.ringFor = ringFor
    this.acceptEvery = acceptEvery
    this.connectWait = connectWait
    this.connectFor = connectFor

    this.state = initiator ? 'dialling' : 'ringing'
    this.startedAt = 0
    this.localStream = null
    this.screenStream = null
    this.peers = new Map() // account -> { pc, stream, senders, state }
    this.muted = false
    this.cameraOff = !video
    this.speaking = new Set()

    // Which way the camera is pointing, so switching knows what to ask for.
    this.facingMode = 'user'
    this.cameraCount = 0

    this._pendingIce = new Map()
    this._detach = room.on('call-signal', (signal) => this._onSignal(signal))
    this._durationTimer = null
    this._closed = false
  }

  get participants () {
    return [...this.peers.keys()]
  }

  get duration () {
    return this.startedAt ? Math.floor((Date.now() - this.startedAt) / 1000) : 0
  }

  /* ------------------------------------------------------------------ */
  /* outgoing                                                           */
  /* ------------------------------------------------------------------ */

  async start () {
    this._capturing = this._captureLocal()
    this.localStream = await this._capturing
    // Their call reached us while the microphone was being asked for, and
    // this one gave way to it (see crossed): there is nothing of ours to ring.
    if (this._closed || this.state !== 'dialling') return this
    this._send({ type: SIGNAL.RING, video: this.video })
    this._ringStarted = Date.now()
    this._ringTimer = setInterval(() => this._ringAgain(), this.ringEvery)
    if (this._ringTimer.unref) this._ringTimer.unref()
    this.emit('state', this.state)
    return this
  }

  /**
   * Offer the call again, or give up on it.
   *
   * A minute of ringing with no answer is a missed call, as it is on every
   * phone. Before this the caller rang forever, or until they gave up and
   * pressed the red button themselves.
   */
  _ringAgain () {
    if (this._closed || this.state !== 'dialling') return this._stopRinging()
    if (Date.now() - this._ringStarted >= this.ringFor) {
      this._stopRinging()
      this.hangUp()
      return
    }
    this._send({ type: SIGNAL.RING, video: this.video })
  }

  _stopRinging () {
    if (this._ringTimer) clearInterval(this._ringTimer)
    this._ringTimer = null
  }

  /**
   * Two people pressed call at the same moment, and this call gives way.
   *
   * Each phone was dialling and took the other's ring for somebody calling
   * into the call it was already in, so it answered "I'm busy in this one"
   * and went on dialling. Neither rang, neither connected, and a minute later
   * both gave up — each believing the other had not picked up.
   *
   * Now the two calls become one: the one with the lower id stays, and this
   * side — the higher — joins it as if it had been rung and picked up,
   * microphone and screen as they are. CallManager decides which; both ends
   * apply the same rule, so exactly one of them gives way.
   */
  async crossed (callId) {
    if (this._closed || this.state !== 'dialling' || !callId || callId === this.id) return false
    const own = this.id
    this._stopRinging()
    // Anybody still being rung by ours stops: it is not going to be answered.
    this._send({ type: SIGNAL.END })
    this.id = callId
    this.initiator = false
    this.joined = true
    this.declinedBy.clear()
    this._setState('connecting')
    this.localStream = await (this._capturing || this._captureLocal())
    if (this._closed) return false
    this._send({ type: SIGNAL.ACCEPT, video: this.video })
    this._repeatAccept()
    this.emit('crossed', { from: own, to: callId })
    return true
  }

  /**
   * Answer an incoming call — or walk into one that is already running.
   *
   * The ACCEPT is a broadcast, and everyone already in the call answers it
   * with an offer. That is the whole of joining: a newcomer does not need to
   * be told who else is there, and the people who are there do not need to
   * agree among themselves about who handles the newcomer.
   */
  async accept () {
    this.joined = true
    this.localStream = await this._captureLocal()
    this._setState('connecting')
    this._send({ type: SIGNAL.ACCEPT, video: this.video })
    this._repeatAccept()
    return this
  }

  /** ACCEPT again until somebody offers; see ACCEPT_EVERY. */
  _repeatAccept () {
    if (this._acceptTimer) return
    const stop = () => {
      clearInterval(this._acceptTimer)
      this._acceptTimer = null
    }
    this._acceptTimer = setInterval(() => {
      if (this._closed || this.state !== 'connecting' || this.peers.size > 0) return stop()
      this._send({ type: SIGNAL.ACCEPT, video: this.video })
    }, this.acceptEvery)
    if (this._acceptTimer.unref) this._acceptTimer.unref()
  }

  decline () {
    this._send({ type: SIGNAL.DECLINE })
    this._finish(CALL_STATE.DECLINED)
  }

  /**
   * Leaving.
   *
   * In a one-to-one call there is nothing left when one side goes, so this
   * ends it. In a group it must not: hanging up used to send END whenever
   * fewer than two connections were up, which in a group call is everybody
   * except the middle of it — one person leaving took the call down for the
   * rest.
   *
   * The exception is a call nobody has answered yet, where END is what stops
   * every other phone ringing.
   */
  hangUp () {
    const leaving = this.group && this.joined && !!this.startedAt
    this._send({ type: leaving ? SIGNAL.LEAVE : SIGNAL.END })
    this._finish(this.startedAt ? CALL_STATE.ENDED : CALL_STATE.MISSED)
  }

  /* ------------------------------------------------------------------ */
  /* media controls                                                     */
  /* ------------------------------------------------------------------ */

  setMuted (muted) {
    this.muted = muted
    for (const track of this.localStream ? this.localStream.getAudioTracks() : []) {
      track.enabled = !muted
    }
    this._send({ type: SIGNAL.MEDIA, muted, cameraOff: this.cameraOff })
    this.emit('media', { muted, cameraOff: this.cameraOff })
  }

  /**
   * Turn the camera on or off mid-call.
   *
   * Toggling `track.enabled` is only enough when a camera track already
   * exists. A call that started as audio has none — getUserMedia was asked for
   * audio alone — so the button did nothing at all: no error, no picture, no
   * way to tell it had been pressed. Switching video on therefore has to
   * acquire the camera, attach it to every peer connection and renegotiate,
   * which is the one place in a call where the media changes after the
   * handshake.
   */
  async setCameraOff (off) {
    const haveVideo = this.localStream ? this.localStream.getVideoTracks().length > 0 : false

    if (!off && !haveVideo) {
      const capture = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' }
      })
      const track = capture.getVideoTracks()[0]
      if (!track) return

      if (!this.localStream) this.localStream = capture
      else this.localStream.addTrack(track)

      // The call is a video call from now on: the UI hangs the local preview
      // tile off this flag.
      this.video = true
      this.cameraOff = false
      this.countCameras().catch(() => {})

      const added = await this._sendVideoTrack(track)

      this.emit('local-stream', this.localStream)
      if (added) await this._renegotiate()
      this._send({ type: SIGNAL.MEDIA, muted: this.muted, cameraOff: false })
      this.emit('media', { muted: this.muted, cameraOff: false })
      return
    }

    this.cameraOff = off
    for (const track of this.localStream ? this.localStream.getVideoTracks() : []) {
      track.enabled = !off
    }
    this._send({ type: SIGNAL.MEDIA, muted: this.muted, cameraOff: off })
    this.emit('media', { muted: this.muted, cameraOff: off })
  }

  /**
   * How many cameras this device has.
   *
   * Worth asking rather than assuming: a browser will not name its video
   * inputs until it has been given permission to use one, so this is only
   * meaningful once a call is running, and it is what decides whether the UI
   * offers a switch button at all. A laptop with one webcam should not have
   * one; a phone should.
   */
  async countCameras () {
    const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : null
    if (!media || !media.enumerateDevices) return 0
    const devices = await media.enumerateDevices().catch(() => [])
    const count = devices.filter((d) => d.kind === 'videoinput').length
    /*
     * Always announced, from here, whoever asked.
     *
     * It used to be announced only by the first capture — and in an audio call
     * that capture asked for no camera, so the browser had not yet been
     * allowed to look and reported a single anonymous video input, as it does
     * for every page before permission. Turning the camera on later counted
     * again and got the right answer, and told nobody: the switch button went
     * on believing there was one camera and stayed hidden for the rest of the
     * call.
     */
    if (count !== this.cameraCount) {
      this.cameraCount = count
      this.emit('devices', count)
    }
    return count
  }

  /**
   * Point the other camera at whoever is in front of it.
   *
   * Two different mechanisms, because two different kinds of device. A phone
   * reports a facingMode on its track and honours a request for the other
   * side; a laptop with two webcams reports no facing mode at all and has to
   * be cycled by device id. Asking for facingMode on the laptop would quietly
   * hand back the same camera, which looks exactly like a button that does
   * nothing.
   *
   * The old track is stopped before the new one is opened. That is not tidying
   * up: most Android devices will not open the back camera while the front one
   * is still held, and the request simply fails. If it fails anyway the old
   * camera is reopened, so a refused switch does not end up as a black square.
   */
  async switchCamera () {
    const current = this.localStream ? this.localStream.getVideoTracks()[0] : null
    if (!current) throw new Error('The camera is off')
    if (this.screenStream) throw new Error('Stop sharing the screen first')

    const settings = current.getSettings ? current.getSettings() : {}
    const wanted = await this._nextCameraConstraint(settings)
    if (!wanted) throw new Error('This device has only one camera')

    const previous = settings.deviceId
      ? { deviceId: { exact: settings.deviceId } }
      : { facingMode: { ideal: settings.facingMode || this.facingMode } }

    current.stop()
    this.localStream.removeTrack(current)

    let track = null
    try {
      track = await this._openCamera(wanted)
    } catch (err) {
      // Put back what was there. The person pressed a button and should not
      // lose the picture they already had because of it.
      const back = await this._openCamera(previous).catch(() => null)
      if (back) this._adoptVideoTrack(back)
      throw err
    }

    this._adoptVideoTrack(track)
    if (track.getSettings && track.getSettings().facingMode) {
      this.facingMode = track.getSettings().facingMode
    }
    // The preview is mirrored for a front camera and must not be for a back
    // one — see the `mirrored` class in the call surface.
    this.emit('facing', this.facingMode)
    return this.facingMode
  }

  async _nextCameraConstraint (settings) {
    // A phone: flip the side it is looking at.
    if (settings.facingMode) {
      const other = settings.facingMode === 'environment' ? 'user' : 'environment'
      this.facingMode = other
      return { facingMode: { exact: other } }
    }

    // Everything else: the next video input along.
    const devices = (typeof navigator !== 'undefined' && navigator.mediaDevices
      ? await navigator.mediaDevices.enumerateDevices().catch(() => [])
      : []).filter((d) => d.kind === 'videoinput')
    this.cameraCount = devices.length
    if (devices.length < 2) return null

    const at = devices.findIndex((d) => d.deviceId && d.deviceId === settings.deviceId)
    const next = devices[(at + 1) % devices.length]
    if (!next || !next.deviceId || next.deviceId === settings.deviceId) return null
    return { deviceId: { exact: next.deviceId } }
  }

  async _openCamera (constraint) {
    const video = { width: { ideal: 1280 }, height: { ideal: 720 }, ...constraint }
    let capture
    try {
      capture = await navigator.mediaDevices.getUserMedia({ video })
    } catch (err) {
      // `exact` is a demand and throws when it cannot be met. Ask again as a
      // preference rather than giving up: on some devices the front camera is
      // reported without a facing mode and the exact form never matches.
      if (err && err.name === 'OverconstrainedError' && constraint.facingMode) {
        capture = await navigator.mediaDevices.getUserMedia({
          video: { ...video, facingMode: { ideal: constraint.facingMode.exact } }
        })
      } else {
        throw err
      }
    }
    const track = capture.getVideoTracks()[0]
    if (!track) throw new Error('That camera gave no picture')
    return track
  }

  /** Put a new video track into the local stream and into every connection. */
  _adoptVideoTrack (track) {
    for (const old of this.localStream.getVideoTracks()) {
      old.stop()
      this.localStream.removeTrack(old)
    }
    this.localStream.addTrack(track)
    this._sendVideoTrack(track).catch(() => {})
    this.emit('local-stream', this.localStream)
  }

  /**
   * Offer again after the local media changed.
   *
   * Only from a stable connection: an offer sent while one is already in
   * flight is rejected outright, and skipping is safe because the side that
   * has an offer open is about to renegotiate anyway.
   */
  async _renegotiate () {
    for (const [account, peer] of this.peers) {
      if (peer.pc.signalingState !== 'stable') continue
      try {
        const offer = await peer.pc.createOffer()
        await peer.pc.setLocalDescription(offer)
        this._send({ type: SIGNAL.OFFER, sdp: offer.sdp, to: account }, { to: peer.transportPeer })
      } catch { /* the connection went away underneath us */ }
    }
  }

  /** Whether this runtime can capture a screen at all. */
  static get screenShareSupported () {
    return typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getDisplayMedia === 'function'
  }

  get screenSharing () {
    return !!this.screenStream
  }

  /**
   * Swap the outgoing video track for a screen capture, and back.
   *
   * The awkward case is sharing from a call that has no camera on: there is no
   * video sender to swap, so a track has to be *added* — and adding a track
   * changes the shape of the session, which the other side only learns about
   * from a fresh offer. Without that renegotiation the sharing side saw its
   * own preview and everybody else saw nothing at all, which is the least
   * useful way for a feature to fail.
   */
  async setScreenShare (on) {
    if (on && !Call.screenShareSupported) {
      throw new Error('This device cannot share its screen')
    }

    if (on) {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: false
      })
      this.screenStream = stream
      const track = stream.getVideoTracks()[0]
      // The browser's own "stop sharing" bar ends the track without telling
      // us, so the call has to notice and put the camera back itself.
      track.addEventListener('ended', () => { this.setScreenShare(false).catch(() => {}) })

      if (!this.localStream) this.localStream = new MediaStream()
      const added = await this._sendVideoTrack(track)
      this.video = true
      if (added) await this._renegotiate()
      this.emit('local-stream', this.localStream)
    } else {
      if (this.screenStream) {
        for (const t of this.screenStream.getTracks()) t.stop()
        this.screenStream = null
      }
      const camera = this.localStream ? this.localStream.getVideoTracks()[0] : null
      await this._sendVideoTrack(camera && !this.cameraOff ? camera : null)
      // Back to whatever the call was before: a video call if a camera is on,
      // an audio call otherwise.
      this.video = !!camera && !this.cameraOff
    }

    this.emit('screen-share', this.screenSharing)
    this.emit('media', { muted: this.muted, cameraOff: this.cameraOff })
  }

  /**
   * Send this video track to every peer, or none to send nothing.
   *
   * The sender is remembered on the peer rather than looked up each time.
   * getSenders() can only find a video sender by the track it is carrying, so
   * once the track has been cleared the sender becomes invisible to that
   * search — and the next call would add a *second* one, giving the other side
   * two video streams where it wanted one.
   *
   * @returns true when a sender had to be created, which is the case that
   *   needs a fresh offer afterwards.
   */
  async _sendVideoTrack (track) {
    let added = false
    for (const peer of this.peers.values()) {
      const sender = peer.videoSender ||
        peer.pc.getSenders().find((s) => s.track && s.track.kind === 'video') || null

      if (sender) {
        peer.videoSender = sender
        await sender.replaceTrack(track).catch(() => {})
      } else if (track) {
        peer.videoSender = peer.pc.addTrack(track, this.localStream)
        added = true
      }
    }
    // A sender that has only just appeared has none of the sizing on it yet.
    if (added) this._applyQuality()
    return added
  }

  /* ------------------------------------------------------------------ */
  /* signalling                                                         */
  /* ------------------------------------------------------------------ */

  _send (payload, opts) {
    this.room.sendCallSignal({ ...payload, callId: this.id }, opts)
  }

  async _onSignal (signal) {
    if (this._closed) return
    if (signal.callId !== this.id) return
    if (signal.from === this.room.me) return

    // Where to reach this person now. After a change of network the room
    // talks over a new link, and whatever is addressed to them has to follow.
    const known = this.peers.get(signal.from)
    if (known && signal.peer) known.transportPeer = signal.peer

    switch (signal.type) {
      case SIGNAL.ACCEPT:
        return this._onAccept(signal)
      case SIGNAL.DECLINE:
        return this._onDecline(signal)
      case SIGNAL.OFFER:
        return this._onOffer(signal)
      case SIGNAL.ANSWER:
        return this._onAnswer(signal)
      case SIGNAL.ICE:
        return this._onIce(signal)
      case SIGNAL.MEDIA:
        this.emit('peer-media', signal.from, { muted: signal.muted, cameraOff: signal.cameraOff })
        return
      case SIGNAL.LEAVE:
        return this._dropPeer(signal.from)
      case SIGNAL.END:
        /*
         * END means "this call is over" from the person who sent it. In a
         * one-to-one call that is the whole call. In a group it is only true
         * if there is nobody else left — otherwise it is somebody leaving, and
         * the people still talking should carry on.
         */
        if (!this.joined || !this.group || this.peers.size <= 1) {
          return this._finish(CALL_STATE.ENDED)
        }
        return this._dropPeer(signal.from)
    }
  }

  /**
   * Somebody said no.
   *
   * A group call must survive it. The old rule — end the call if no
   * connection is up yet — meant the first person to decline cancelled the
   * call for everyone else who was still being rung.
   */
  _onDecline (signal) {
    this.declinedBy.add(signal.from)
    this.emit('declined', signal.from, signal.reason || '')
    if (this.peers.size > 0) return

    // Turned away from a full call. Nothing is going to change by waiting, and
    // a group call would otherwise leave the newcomer sitting on "connecting"
    // for as long as they could stand it.
    if (signal.reason === 'full') return this._finish(CALL_STATE.DECLINED)

    // Everybody who could have answered has said no.
    const others = this.members - 1
    if (!this.group || this.declinedBy.size >= others) this._finish(CALL_STATE.DECLINED)
  }

  /**
   * Somebody picked up. Everyone already in the call offers them a connection.
   *
   * An ACCEPT is a broadcast and the sender does not hear their own, so this
   * runs on every *other* phone in the room — including the ones still
   * ringing, which is where two bugs lived.
   *
   * The first: a phone that had not answered yet built a peer connection
   * anyway. It had no microphone open at that point, so it joined the call as
   * a silent participant with no picture and no sound, before its owner had
   * touched anything. That is most of "group calls don't work".
   *
   * The second: who offers. The rule was `initiator || me < from`, and outside
   * a two-person call it decides nothing reliable — when the third person
   * accepts, the second only offers if their account key happens to sort
   * first, and otherwise nobody does and those two never connect. Which pairs
   * of people in a group call could hear each other came down to how their
   * keys sorted.
   *
   * The rule now is simply: if I am in the call, I offer to whoever just
   * joined. One offer per pair, no ordering to get wrong, and a newcomer needs
   * no list of who is already here. Two people accepting in the same instant
   * do glare — both offer — and that is what the rollback in _onOffer is for.
   */
  async _onAccept (signal) {
    // Still being rung: this is somebody else answering, and none of it is
    // ours to negotiate until our own owner picks up.
    if (!this.joined) return

    // Their accept again: our offer did not reach them (see ACCEPT_EVERY).
    if (this.peers.has(signal.from)) return this._offerAgain(signal.from)

    if (this.peers.size >= MESH_LIMIT - 1) {
      // The mesh is full. Say so, rather than letting it look like somebody
      // hung up on them.
      this._send({ type: SIGNAL.DECLINE, reason: 'full' }, { to: signal.peer })
      return
    }
    // They picked up. The caller sat on "dialling" until ICE completed, which
    // reads as an unanswered call for as long as the connection takes.
    if (this.state === 'dialling') this._setState('connecting')

    await this._iceReady
    if (this._closed) return
    // A repeat that arrived while the servers were awaited.
    if (this.peers.has(signal.from)) return this._offerAgain(signal.from)
    const peer = this._ensurePeer(signal.from, signal.peer)

    const offer = await peer.pc.createOffer()
    await peer.pc.setLocalDescription(offer)
    this._send({ type: SIGNAL.OFFER, sdp: offer.sdp, to: signal.from }, { to: signal.peer })
  }

  /**
   * Send the offer still waiting for an answer once more, to the whole room
   * this time: the link it went down first may be the one that lost it.
   */
  _offerAgain (account) {
    const peer = this.peers.get(account)
    if (!peer || peer.pc.signalingState !== 'have-local-offer' || !peer.pc.localDescription) return
    this._send({ type: SIGNAL.OFFER, sdp: peer.pc.localDescription.sdp, to: account })
  }

  async _onOffer (signal) {
    if (signal.to && signal.to !== this.room.me) return
    // Nobody should be offering us media before we have answered; if they do,
    // they are talking to a phone that is still ringing.
    if (!this.joined) return
    await this._iceReady
    if (this._closed) return
    const peer = this._ensurePeer(signal.from, signal.peer)

    // The same offer again: our answer did not reach them. Answer again
    // rather than negotiate a second time.
    const remote = peer.pc.remoteDescription
    if (peer.pc.signalingState === 'stable' && remote && remote.type === 'offer' &&
        remote.sdp === signal.sdp && peer.pc.localDescription) {
      this._send({ type: SIGNAL.ANSWER, sdp: peer.pc.localDescription.sdp, to: signal.from })
      return
    }

    if (peer.pc.signalingState !== 'stable') {
      // Both sides renegotiated at once — switching the camera on together
      // will do it. The lower account id keeps its offer and this one is
      // dropped; the other rolls back and answers, so exactly one survives.
      if (this.room.me < signal.from) return
      await peer.pc.setLocalDescription({ type: 'rollback' }).catch(() => {})
    }

    await peer.pc.setRemoteDescription({ type: 'offer', sdp: signal.sdp })
    await this._flushIce(peer, signal.from)

    const answer = await peer.pc.createAnswer()
    await peer.pc.setLocalDescription(answer)
    this._send({ type: SIGNAL.ANSWER, sdp: answer.sdp, to: signal.from }, { to: signal.peer })
  }

  async _onAnswer (signal) {
    if (signal.to && signal.to !== this.room.me) return
    const peer = this.peers.get(signal.from)
    if (!peer) return
    // An answer to an offer that is already answered: a repeat (see
    // _onOffer). Applying it again throws, and nothing needs it.
    if (peer.pc.signalingState !== 'have-local-offer') return
    await peer.pc.setRemoteDescription({ type: 'answer', sdp: signal.sdp })
    await this._flushIce(peer, signal.from)
  }

  async _onIce (signal) {
    if (signal.to && signal.to !== this.room.me) return
    const peer = this.peers.get(signal.from)
    if (!peer || !peer.pc.remoteDescription) {
      const queue = this._pendingIce.get(signal.from) || []
      queue.push(signal.candidate)
      this._pendingIce.set(signal.from, queue)
      return
    }
    await peer.pc.addIceCandidate(signal.candidate).catch(() => {})
  }

  async _flushIce (peer, account) {
    const queue = this._pendingIce.get(account)
    if (!queue) return
    this._pendingIce.delete(account)
    for (const candidate of queue) {
      await peer.pc.addIceCandidate(candidate).catch(() => {})
    }
  }

  /**
   * Size the outgoing video for the number of people receiving it.
   *
   * Called whenever the participant count changes. Without it a mesh call
   * sends every listener the same 1.2 Mbit/s stream it would send a single
   * one, so the sixth person to join is asking a phone for 6 Mbit/s of uplink
   * — and what actually happens is that every stream stutters at once.
   *
   * setParameters is a request, not a promise: a browser may round the
   * resolution or ignore the cap. Failures are swallowed because a call with
   * unsized video is still a call.
   */
  _applyQuality () {
    const n = this.peers.size
    const profile = QUALITY.find((q) => n <= q.upTo) || QUALITY[QUALITY.length - 1]

    for (const peer of this.peers.values()) {
      const sender = peer.videoSender
      if (!sender || typeof sender.getParameters !== 'function') continue
      try {
        const params = sender.getParameters()
        if (!params.encodings || !params.encodings.length) params.encodings = [{}]
        params.encodings[0].maxBitrate = profile.bitrate
        params.encodings[0].scaleResolutionDownBy = profile.scale
        sender.setParameters(params).catch(() => {})
      } catch { /* the sender went away between the two lines */ }
    }
    return profile
  }

  /* ------------------------------------------------------------------ */
  /* peer connections                                                   */
  /* ------------------------------------------------------------------ */

  _pcConfig () {
    return { iceServers: this.iceServers, bundlePolicy: 'max-bundle', rtcpMuxPolicy: 'require' }
  }

  /**
   * Start finding a way to this person while the call is still ringing.
   *
   * A connection used to be made only once somebody picked up, and only
   * then did it start gathering its addresses: a round trip to each STUN
   * server and, between two phones on mobile data, the allocation on the
   * TURN server that the call goes through, several round trips over a
   * mobile link. All of it sat between "answer" and hearing anybody. A
   * connection made with a candidate pool gathers all that at once, so
   * here one is made for the other person the moment the call starts
   * ringing, on both phones, and the real connection takes it over
   * (_ensurePeer). Nothing is sent from it until then.
   *
   * One-to-one calls only: in a group nobody knows who will pick up.
   */
  async warm (account) {
    if (!account || account === this.room.me || this.group) return false
    if (this._warm.has(account) || this.peers.has(account)) return false
    // Taken before the wait, so a second warm() for the same person does
    // not make a second connection.
    this._warm.set(account, null)
    await this._iceReady
    if (this._closed || this.peers.has(account) || this._warm.get(account) !== null) {
      if (this._warm.get(account) === null) this._warm.delete(account)
      return false
    }
    try {
      this._warm.set(account, new RTCPeerConnection({ ...this._pcConfig(), iceCandidatePoolSize: 1 }))
      return true
    } catch {
      this._warm.delete(account)
      return false
    }
  }

  _takeWarm (account) {
    const pc = this._warm.get(account)
    this._warm.delete(account)
    return pc && pc.signalingState !== 'closed' ? pc : null
  }

  _closeWarm () {
    for (const pc of this._warm.values()) {
      try { if (pc) pc.close() } catch {}
    }
    this._warm.clear()
  }

  _ensurePeer (account, transportPeer) {
    const existing = this.peers.get(account)
    if (existing) return existing

    const pc = this._takeWarm(account) || new RTCPeerConnection(this._pcConfig())

    const peer = {
      pc, account, transportPeer, stream: new MediaStream(), state: 'new', videoSender: null
    }
    this.peers.set(account, peer)

    // The screen, when one is being shared, stands in for the camera: this is
    // a peer arriving mid-share, and it should see what everybody else does.
    const screen = this.screenStream ? this.screenStream.getVideoTracks()[0] : null
    if (this.localStream) {
      for (const track of this.localStream.getTracks()) {
        if (track.kind === 'video' && screen) continue
        const sender = pc.addTrack(track, this.localStream)
        if (track.kind === 'video') peer.videoSender = sender
      }
    }
    if (screen) peer.videoSender = pc.addTrack(screen, this.localStream || new MediaStream())

    pc.onicecandidate = (event) => {
      if (!event.candidate) return
      // peer.transportPeer, not the one this connection started with: after
      // the network changes the room is reached over a new link, and a
      // candidate sent down the old one is lost (see _onSignal).
      this._send({ type: SIGNAL.ICE, candidate: event.candidate.toJSON(), to: account }, { to: peer.transportPeer })
    }

    pc.ontrack = (event) => {
      for (const stream of event.streams) {
        peer.stream = stream
      }
      this.emit('stream', account, peer.stream)
    }

    /*
     * Not up after CONNECT_WAIT: offer a new path. A failed connection
     * already does (below); one stuck on "connecting", for want of the
     * other side's candidates or answer, never fails and never mended.
     */
    const wait = setTimeout(() => {
      if (this._closed || this.peers.get(account) !== peer) return
      if (pc.connectionState !== 'connected') this._heal(account)
    }, this.connectWait)
    if (wait.unref) wait.unref()

    pc.onconnectionstatechange = () => {
      peer.state = pc.connectionState
      if (pc.connectionState === 'connected') peer.everConnected = true
      this.emit('peer-state', account, pc.connectionState)

      if (pc.connectionState === 'connected' && !this.startedAt) {
        this.startedAt = Date.now()
        this._setState('active')
        this._startDurationTimer()
      }
      if (pc.connectionState === 'connected') this._reportPath(account, pc)
      if (pc.connectionState === 'failed') this._heal(account)
      if (pc.connectionState === 'disconnected') {
        setTimeout(() => {
          if (pc.connectionState === 'disconnected') this._heal(account)
        }, HEAL_AFTER)
      }
      if (pc.connectionState === 'closed') {
        this._dropPeer(account)
      }
    }

    this.emit('peer-joined', account)
    this._applyQuality()
    return peer
  }

  /**
   * Which way the media went: straight between the two devices, or through
   * the TURN server. The first question to ask of a call that took long to
   * connect or sounds bad, and nothing else in the app can answer it; the
   * Android shell writes it into its journal (see the UI's call wiring).
   */
  async _reportPath (account, pc) {
    if (typeof pc.getStats !== 'function') return
    try {
      const stats = await pc.getStats()
      const byId = new Map()
      let pair = null
      stats.forEach((entry) => {
        byId.set(entry.id, entry)
        if (entry.type === 'transport' && entry.selectedCandidatePairId) pair = entry.selectedCandidatePairId
      })
      if (!pair) {
        stats.forEach((entry) => {
          if (!pair && entry.type === 'candidate-pair' && entry.nominated && entry.state === 'succeeded') pair = entry.id
        })
      }
      const chosen = pair && byId.get(pair)
      if (!chosen) return
      const local = byId.get(chosen.localCandidateId) || {}
      const remote = byId.get(chosen.remoteCandidateId) || {}
      this.emit('path', account, {
        local: local.candidateType || '?',
        remote: remote.candidateType || '?',
        protocol: local.relayProtocol || local.protocol || '?',
        rtt: typeof chosen.currentRoundTripTime === 'number' ? Math.round(chosen.currentRoundTripTime * 1000) : null
      })
    } catch { /* closed while asking */ }
  }

  /**
   * The phone's network changed (the Android shell says so). Every path the
   * media had went with the old network; start finding new ones at once
   * rather than wait for each connection to notice.
   */
  networkChanged () {
    for (const account of this.peers.keys()) this._heal(account)
  }

  /**
   * Find the media a new way to the other side.
   *
   * WebRTC sees quickly that a path stopped working; what it cannot do is
   * agree a new one on its own. A restart is a new offer, and the offer has
   * to reach the other phone — `pc.restartIce()`, which is all this used to
   * do, only marks the next offer as a restart, and no next offer was ever
   * made. A call survived a change of network only if the old path happened
   * to keep working.
   *
   * The offer goes to the whole room, addressed by account: the link it
   * would have taken may be the one that just broke. It is tried again every
   * few seconds — right after a change of network the room itself is being
   * reconnected, and an offer sent before that is lost — and given up on
   * after HEAL_FOR, which ends a two-person call rather than leave it on a
   * frozen picture. One side makes the offers, the lower account id, the
   * same one that wins when two offers cross (_onOffer); the other answers.
   */
  _heal (account) {
    const peer = this.peers.get(account)
    if (!peer || peer.healing || this._closed) return
    const started = Date.now()
    const stop = () => {
      clearInterval(timer)
      peer.healing = null
    }
    const attempt = async () => {
      if (this._closed || this.peers.get(account) !== peer) return stop()
      if (peer.pc.connectionState === 'connected') return stop()
      if (Date.now() - started > HEAL_FOR) {
        stop()
        return this._dropPeer(account)
      }
      if (this.room.me > account) return
      try {
        if (peer.pc.signalingState === 'have-local-offer') {
          await peer.pc.setLocalDescription({ type: 'rollback' })
        }
        if (peer.pc.signalingState !== 'stable') return
        const offer = await peer.pc.createOffer({ iceRestart: true })
        await peer.pc.setLocalDescription(offer)
        this._send({ type: SIGNAL.OFFER, sdp: offer.sdp, to: account })
      } catch { /* the connection is going away; the next attempt will see */ }
    }
    const timer = setInterval(attempt, HEAL_EVERY)
    if (timer.unref) timer.unref()
    peer.healing = { stop }
    attempt()
  }

  _dropPeer (account) {
    const peer = this.peers.get(account)
    if (!peer) return
    if (peer.healing) peer.healing.stop()
    try { peer.pc.close() } catch {}
    this.peers.delete(account)
    this.emit('peer-left', account)
    this._applyQuality()

    // The last one out ends the call. In a group that is the person who
    // stayed longest, not the first to leave.
    if (this.peers.size === 0) this._finish(CALL_STATE.ENDED)
  }

  async _captureLocal () {
    if (this.localStream) return this.localStream
    const constraints = {
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: this.video ? { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' } : false
    }
    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    // Only now are the video inputs nameable — a browser hides them until it
    // has granted a capture — so this is the first moment the UI can be told
    // whether there is a second camera to switch to.
    this.countCameras().then(() => this.emit('devices', this.cameraCount)).catch(() => {})
    this.emit('local-stream', stream)
    return stream
  }

  _startDurationTimer () {
    if (this._durationTimer) return
    this._durationTimer = setInterval(() => this.emit('tick', this.duration), 1000)
  }

  _setState (state) {
    if (this.state === state) return
    this.state = state
    if (state !== 'dialling') this._stopRinging()
    if (state === 'connecting') this._watchConnect()
    this.emit('state', state)
  }

  /**
   * End a call that never connects, and say why ('unreachable'), instead
   * of leaving both people on "connecting" until one of them gives up.
   * The others are told the way hanging up would: END in a one-to-one
   * call, LEAVE in a group, where the call goes on without us.
   */
  _watchConnect () {
    if (this._connectTimer) return
    this._connectTimer = setTimeout(() => {
      this._connectTimer = null
      if (this._closed || this.startedAt) return
      this.emit('unreachable')
      this._send({ type: this.group ? SIGNAL.LEAVE : SIGNAL.END })
      this._finish(CALL_STATE.ENDED)
    }, this.connectFor)
    if (this._connectTimer.unref) this._connectTimer.unref()
  }

  async _finish (result) {
    if (this._closed) return
    this._closed = true
    this._stopRinging()
    if (this._connectTimer) clearTimeout(this._connectTimer)
    if (this._acceptTimer) clearInterval(this._acceptTimer)
    this._wereHere = [...this.peers.keys()]

    const duration = this.duration
    if (this._durationTimer) clearInterval(this._durationTimer)
    if (this._detach) this._detach()

    for (const peer of this.peers.values()) {
      if (peer.healing) peer.healing.stop()
      try { peer.pc.close() } catch {}
    }
    this.peers.clear()
    this._closeWarm()

    for (const stream of [this.localStream, this.screenStream]) {
      if (stream) for (const t of stream.getTracks()) t.stop()
    }
    this.localStream = this.screenStream = null

    this._setState('ended')
    this.emit('ended', { result, duration })

    /*
     * Written into the chat by whoever was in the call — the caller always,
     * the person rung only if they picked up. A phone that only rang used to
     * write it too, as "ended, 0:00", beside the caller's "missed": two lines
     * for one call, and the one that said what happened was the one people
     * did not read. Both ends of a call that did connect write it, and the
     * room keeps one (apply's call log).
     */
    if (this.room.writable && this.joined) {
      this.room.logCall({
        callId: this.id,
        state: result,
        video: this.video,
        startedAt: this.startedAt ? fromDevice(this.startedAt) : clockNow(),
        duration,
        participants: this._wereHere || []
      }).catch(() => {})
    }
  }
}

/**
 * Watches every open room for incoming calls and keeps at most one active.
 */
export class CallManager extends Emitter {
  /**
   * @param iceServers RTCIceServer[], or a function that resolves to them —
   *   asked before every call, so TURN credentials can be kept fresh
   *   (see ice.js).
   */
  constructor (client, { iceServers = DEFAULT_ICE_SERVERS } = {}) {
    super()
    this.client = client
    this.iceServers = iceServers
    this.active = null
    this.incoming = null
    this._seen = new Set()
    this._asking = new Map() // roomId -> resolve, while a HELLO is outstanding

    client.on('call-signal', (roomId, signal) => this._onSignal(roomId, signal))
  }

  get supported () {
    return hasWebRTC
  }

  /**
   * Ask for TURN credentials ahead of a call: the Android shell calls this
   * when a push or its alarm wakes the phone, so a call that rings a moment
   * later finds them ready.
   */
  prefetchIce () {
    if (typeof this.iceServers === 'function') this._ice().catch(() => {})
  }

  /** The ICE servers for a call starting now. */
  async _ice () {
    if (typeof this.iceServers !== 'function') return this.iceServers
    try {
      const servers = await this.iceServers()
      return Array.isArray(servers) && servers.length ? servers : DEFAULT_ICE_SERVERS
    } catch {
      return DEFAULT_ICE_SERVERS
    }
  }

  /**
   * Start a call — or join the one already happening in this room.
   *
   * In a group, pressing the call button when three people are already
   * talking should put you in with them, not start a second call beside the
   * first. Nothing on the network knows what calls exist, so the room is
   * asked: a HELLO goes out and anybody in a call answers with its id. A
   * moment's wait is worth it; starting a rival call is not recoverable from
   * inside the app.
   */
  async startCall (roomId, { video = false } = {}) {
    if (this.active) throw new Error('Already in a call')
    // They are calling us from this very chat: pressing call is picking up.
    if (this._ringingIn(roomId)) return this.accept()
    const room = await this.client.openRoom(roomId)
    const members = await this._memberCount(room)

    if (members > 2) {
      const running = await this._askForCall(room, roomId)
      if (running) return this.join(roomId, { ...running, video })
    }
    // And again: their ring may have arrived while the room was opening.
    if (this._ringingIn(roomId)) return this.accept()
    if (this.active) throw new Error('Already in a call')

    // Not awaited: the call rings while the credentials are on their way
    // (see Call's constructor).
    const iceServers = this._ice()
    const callId = randomCallId()
    const call = new Call(room, {
      callId, video, members, initiator: true, iceServers
    })
    this._bind(call, roomId)
    this.active = call
    await call.start()
    this._lookWhileRinging(call, room)
    if (!call.group) this._warmFor(call, room)

    // Wake everybody else in the room. Whoever has the app open hears the
    // RING straight away; whoever does not is woken by a push and hears one
    // of the repeats a few seconds later (see RING_EVERY).
    //
    // And again, for as long as it rings. One push that arrived while the
    // phone could not start the app — Doze, a reboot, a flaky network — used
    // to be the whole attempt; the call then rang into nothing for a minute.
    if (this.client.wake) this._wakeWhileRinging(call, room)

    this.emit('call', call)
    return call
  }

  /** Warm a connection to the other person of a one-to-one call (Call.warm). */
  _warmFor (call, room) {
    const me = this.client.identity && this.client.identity.id
    Promise.resolve().then(() => room.view.members()).then((list) => {
      const other = list.map((m) => m.account).find((a) => a && a !== me)
      if (other) return call.warm(other)
    }).catch(() => {})
  }

  _ringingIn (roomId) {
    return !!(this.incoming && this.incoming.roomId === roomId && !this.incoming._closed)
  }

  /**
   * A ring for another call in the room we are dialling: the two people
   * called each other at once. The lower call id wins on both ends — the
   * side holding the higher one gives way and joins (Call.crossed), the
   * other points it at its own call, which also covers a phone that cannot
   * compare ids yet: a build from before this answers every such ring with
   * HERE, and HERE while dialling means "join mine".
   */
  async _crossed (call, roomId, signal) {
    if (signal.callId < call.id) {
      this._seen.add(signal.callId)
      await call.crossed(signal.callId)
      return
    }
    const room = await this.client.openRoom(roomId).catch(() => null)
    if (room) {
      room.sendCallSignal({ type: SIGNAL.HERE, callId: call.id, video: call.video }, { to: signal.peer })
    }
  }

  /**
   * Find the person being called, if nobody in the room is reachable.
   *
   * The ring travels over the room's own connection, so it reaches nobody
   * until there is one. After a phone changes network it is a new peer on
   * the DHT, and the caller's swarm only looks it up again on its own slow
   * schedule — minutes, while the call rings for one. Asking the DHT again
   * every few seconds is what lets the two find each other inside that
   * minute.
   */
  _lookWhileRinging (call, room) {
    const transport = this.client.transport
    const topic = room.base && room.base.discoveryKey
    if (!transport || typeof transport.refresh !== 'function' || !topic) return
    const look = () => {
      if (call._closed || call.state !== 'dialling') return clearInterval(timer)
      if (!room.bus || room.bus.peerCount === 0) transport.refresh(topic)
    }
    const timer = setInterval(look, CALL_LOOK_EVERY)
    if (timer.unref) timer.unref()
    look()
  }

  _wakeWhileRinging (call, room) {
    const me = this.client.identity && this.client.identity.id
    const woken = new Set()
    const wake = () => {
      if (call._closed || call.state !== 'dialling') return stop()
      room.view.members().then((list) => {
        const waiting = list.map((m) => m.account)
          .filter((a) => a && a !== me && !call.peers.has(a) && !call.declinedBy.has(a))
        for (const account of waiting) woken.add(account)
        if (waiting.length) this.client.wake(waiting, 'call', { video: !!call.video })
      }).catch(() => {})
    }
    const timer = setInterval(wake, CALL_WAKE_EVERY)
    if (timer.unref) timer.unref()
    let stopped = false
    /*
     * Ringing is over: picked up, given up, declined. A phone that rang on
     * the push alone has not heard any of that over the network, and would
     * ring on for its whole minute; this tells it to stop, and whether to
     * leave a missed call behind.
     */
    const stop = (answered = false) => {
      clearInterval(timer)
      if (stopped) return
      stopped = true
      if (woken.size) this.client.wake([...woken], 'callend', { answered })
    }
    call.on('state', (state) => { if (state !== 'dialling' && state !== 'ended') stop(true) })
    call.on('ended', () => stop(!!call.startedAt))
    wake()
  }

  /** Walk into a call that is already running. */
  async join (roomId, { callId, video = false } = {}) {
    if (this.active) throw new Error('Already in a call')
    const room = await this.client.openRoom(roomId)
    const call = new Call(room, {
      callId,
      video,
      members: await this._memberCount(room),
      initiator: false,
      iceServers: this._ice()
    })
    this._bind(call, roomId)
    this.active = call
    await call.accept()
    this.emit('call', call)
    return call
  }

  /**
   * @returns { callId, video } when somebody answers within the window,
   *   otherwise null. The window is short: everyone who could answer is
   *   already connected to this room, so a reply is one hop away.
   */
  _askForCall (room, roomId, wait = 700) {
    return new Promise((resolve) => {
      const done = (answer) => {
        if (this._asking.get(roomId) !== done) return
        this._asking.delete(roomId)
        clearTimeout(timer)
        resolve(answer)
      }
      const timer = setTimeout(() => done(null), wait)
      if (timer.unref) timer.unref()
      this._asking.set(roomId, done)
      room.sendCallSignal({ type: SIGNAL.HELLO, callId: '' })
    })
  }

  async _memberCount (room) {
    const members = await room.view.members().catch(() => [])
    return Math.max(2, members.length)
  }

  async accept () {
    if (!this.incoming) return null
    const call = this.incoming
    this.incoming = null
    this.active = call

    // Say the ringing has stopped, not only that a call has started. The UI
    // holds the banner and the call surface in separate state, so without this
    // the "X is calling you" banner stayed on screen for the whole call — the
    // person who had just answered was still being told to answer.
    this.emit('incoming', null)
    await call.accept()
    this.emit('call', call)
    return call
  }

  decline () {
    if (!this.incoming) return
    this.incoming.decline()
    this.incoming = null
    this.emit('incoming', null)
  }

  /**
   * Answer — the call ringing now, or the one about to arrive: the phone may
   * have rung on the push before the call itself reached the app, and the
   * person pressed "answer" on that (see PENDING_ANSWER_FOR).
   */
  answerSoon () {
    if (this.incoming) return this.accept()
    this._pendingDecline = 0
    this._pendingAnswer = Date.now()
    return null
  }

  /** Decline — the call ringing now, or the one about to arrive. */
  declineSoon () {
    if (this.incoming) return this.decline()
    this._pendingAnswer = 0
    this._pendingDecline = Date.now()
  }

  hangUp () {
    if (this.active) this.active.hangUp()
  }

  async _onSignal (roomId, signal) {
    // "Is anything running here?" — answered by whoever is in a call in this
    // room, straight back to the asker.
    if (signal.type === SIGNAL.HELLO) {
      const call = this.active
      if (!call || call.roomId !== roomId || !call.joined || call._closed) return
      const room = await this.client.openRoom(roomId).catch(() => null)
      if (room) {
        room.sendCallSignal(
          { type: SIGNAL.HERE, callId: call.id, video: call.video },
          { to: signal.peer }
        )
      }
      return
    }

    if (signal.type === SIGNAL.HERE) {
      const waiting = this._asking.get(roomId)
      if (waiting) return waiting({ callId: signal.callId, video: !!signal.video })
      // "There is a call here already, come in" — said to us while we were
      // ringing nobody: another call in this room got there first (see
      // _crossed). Join it rather than ring on beside it.
      const call = this.active
      if (call && call.roomId === roomId && call.initiator && call.state === 'dialling' &&
          signal.callId && signal.callId !== call.id) {
        this._seen.add(signal.callId)
        await call.crossed(signal.callId)
      }
      return
    }

    /*
     * An offer, addressed to us, for a call this phone is not in: the other
     * side is trying to mend a call we already left. The END that said so
     * was lost — it went over a link that was failing, which is often why
     * they hung up — and without an answer they would sit on a frozen call
     * until their attempts ran out. Tell them again.
     */
    if (signal.type === SIGNAL.OFFER) {
      const me = this.client.identity && this.client.identity.id
      const mine = this.active || this.incoming
      if (me && signal.to === me && signal.callId && (!mine || mine.id !== signal.callId)) {
        const room = await this.client.openRoom(roomId).catch(() => null)
        if (room) room.sendCallSignal({ type: SIGNAL.END, callId: signal.callId }, { to: signal.peer })
      }
      return
    }

    if (signal.type !== SIGNAL.RING) return

    // We are dialling this very room, and so are they.
    const mine = this.active
    if (mine && mine.roomId === roomId && !mine._closed && mine.initiator &&
        mine.state === 'dialling' && signal.callId !== mine.id) {
      return this._crossed(mine, roomId, signal)
    }

    // Ringing already for another call in this room: two people pressed call
    // at once, and one of the two calls is giving way to the other right now.
    // Neither declined nor remembered — the call that stays rings again in a
    // few seconds, and that ring should get through.
    if (this.incoming && this.incoming.roomId === roomId && this.incoming.id !== signal.callId) return

    if (this._seen.has(signal.callId)) return
    this._seen.add(signal.callId)
    if (this._seen.size > 200) this._seen.clear()

    // Already in this very room's call: the ring is from somebody who did not
    // know it was running. Point them at it instead of declining, which is
    // what used to happen and left them ringing an empty room.
    if (this.active && this.active.roomId === roomId && !this.active._closed) {
      const room = await this.client.openRoom(roomId).catch(() => null)
      if (room) {
        room.sendCallSignal(
          { type: SIGNAL.HERE, callId: this.active.id, video: this.active.video },
          { to: signal.peer }
        )
      }
      return
    }

    // Somebody blocked here does not ring. Declined at once, as a busy line
    // would be — the caller is not told why.
    if (this.client.isBlocked && this.client.isBlocked(signal.from)) {
      const room = await this.client.openRoom(roomId).catch(() => null)
      if (room) room.sendCallSignal({ type: SIGNAL.DECLINE, callId: signal.callId })
      return
    }

    if (this.active || this.incoming) {
      // Busy elsewhere: tell the caller rather than letting them ring out.
      const room = await this.client.openRoom(roomId).catch(() => null)
      if (room) room.sendCallSignal({ type: SIGNAL.DECLINE, callId: signal.callId })
      return
    }

    const room = await this.client.openRoom(roomId)
    const members = await this._memberCount(room)
    // Something else started ringing, or a call began, while the room was
    // being opened.
    if (this.active || this.incoming) return
    const call = new Call(room, {
      callId: signal.callId,
      video: !!signal.video,
      members,
      initiator: false,
      // Not awaited: the phone rings first (see Call's constructor).
      iceServers: this._ice()
    })
    this._bind(call, roomId)
    this.incoming = call

    // Already answered, or declined, on the phone's own call screen.
    const fresh = (at) => at && Date.now() - at < PENDING_ANSWER_FOR
    if (fresh(this._pendingDecline)) {
      this._pendingDecline = 0
      return this.decline()
    }
    if (fresh(this._pendingAnswer)) {
      this._pendingAnswer = 0
      return this.accept()
    }

    this.emit('incoming', call, { roomId, from: signal.from })
    if (!call.group) call.warm(signal.from).catch(() => {})
  }

  _bind (call, roomId) {
    call.roomId = roomId
    call.once('ended', () => {
      if (this.active === call) this.active = null
      if (this.incoming === call) this.incoming = null
      this.emit('call', null)
      this.emit('incoming', null)
    })
  }
}

function randomCallId () {
  const bytes = new Uint8Array(32)
  ;(globalThis.crypto || globalThis.msCrypto).getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/** Turn TURN credentials from the deployment into RTCIceServer entries. */
export function iceServersFor ({ host = 'papi.olshub.xyz', username = null, credential = null } = {}) {
  const servers = [
    { urls: 'stun:' + host + ':3478' },
    { urls: 'stun:stun.cloudflare.com:3478' }
  ]
  if (username && credential) {
    servers.push({
      /*
       * UDP first; then plain TCP on the same port, for networks and
       * filters that let no UDP through to the server; then TLS. The TCP
       * one was missing, so TLS on 5349 was the only fallback, and a
       * server whose coturn had not picked up its certificate (it reads it
       * once, at start) offered none: a phone without UDP to the server
       * could not be called at all.
       */
      urls: [
        'turn:' + host + ':3478?transport=udp',
        'turn:' + host + ':3478?transport=tcp',
        'turns:' + host + ':5349?transport=tcp'
      ],
      username,
      credential
    })
  }
  return servers
}
